import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckOutlinedIcon from '@mui/icons-material/Check';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/Error';
import { useNavigate, useParams } from 'react-router';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_BATCH_BY_ID_QUERY, GET_BATCHES_QUERY } from '../../queries/BatchQueries';
import type { Batch } from '../../models/Batch';
import dayjs from "dayjs";
import {
  DataGrid,
  GridColDef,
  GridRowClassNameParams,
  gridClasses,
} from '@mui/x-data-grid';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import useNotifications from "../../hooks/useNotifications/useNotifications";
import { COMPLETE_BATCH_MUTATION, DELETE_BATCH_BY_ID_MUTATION, RECALCULATE_BATCH_MUTATION } from "../../mutations/BatchMutations";
import { GET_INVENTORYITEMS_QUERY } from "../../queries/InventoryItemQueries";
import type { ConsumedInventoryItem } from '../../models/ConsumedInventoryItem';
import { unitTypeToUnits } from '../../models/Item';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import NumberField from '../NumberField';

const INITIAL_PAGE_SIZE = 100;

export default function BatchShow() {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getBatchById] = useLazyQuery(
    GET_BATCH_BY_ID_QUERY);

  const [recalculateBatchForProduct] = useMutation(
    RECALCULATE_BATCH_MUTATION);

  const [completeBatch] = useMutation(
    COMPLETE_BATCH_MUTATION, {
    refetchQueries: [{
      query: GET_BATCHES_QUERY
    }, {
      query: GET_INVENTORYITEMS_QUERY
    }]
  });

  const [deleteBatchById] = useMutation(
    DELETE_BATCH_BY_ID_MUTATION, {
    refetchQueries: [{
      query: GET_BATCHES_QUERY
    }]
  });

  const [batch, setBatch] = useState<Batch | null>(null);
  const [batchProductAmount, setBatchProductAmount] = useState<number>(0);
  const [isRecalculatingBatch, setIsRecalculatingBatch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(batchId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getBatchByIdError } = await getBatchById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getBatchByIdError) {
          setError(getBatchByIdError);
        }

        if (data?.batch) {
          setBatch(data.batch);
          setBatchProductAmount(data.batch.amount);
        } else {
          setError({ message: 'No batch found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [batchId, getBatchById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onNotesChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      setNotes(event.target.value);
    },
    [],
  );

  const handleBack = useCallback(() => {
    navigate('/dashboard/batches');
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/batches/${batchId}/edit`);
  }, [batchId, navigate]);

  const submitBatch = useCallback(async () => {
    const confirmed = await dialogs.confirm(
      'Are you sure you want to do this, HANNAH!?',
      {
        title: `Complete batch?`,
        severity: 'info',
        okText: 'Complete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsSubmitting(true);

      try {
        const { data: result } = await completeBatch({
          variables: {
            batchId: +(batchId || '0'),
            notes: notes
          }
        });

        if (result?.completed) {
          navigate('/dashboard/batches');
        } else {
          notifications.show(
            'Failed to complete batch. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (createError) {
        notifications.show(
          `Failed to complete batch. Reason: ${(createError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );

        throw createError;
      }
    }
  }, [dialogs, completeBatch, batchId, notes, navigate, notifications]);

  const handleDelete = useCallback(async () => {
    if (!batch) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete this batch?`,
      {
        title: `Delete batch?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteBatchById({ variables: { id: batch.id } });

        if (result?.deleted) {
          navigate('/dashboard/batches');

          notifications.show('Batch deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete batch. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete batch. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [batch, dialogs, deleteBatchById, notifications, navigate]);

  const onAmountChange = useCallback(
    async (value: number | null, fieldName: string) => {
      setBatchProductAmount(value || 0);
    },
    [],
  );

  const recalculateBatch = useCallback(async () => {
    if ((batchProductAmount || 0) > 0) {
      setIsRecalculatingBatch(true);

      const { data: result } = await recalculateBatchForProduct({
        variables: {
          batchId: batch?.id || 0,
          amount: batchProductAmount
        }
      });

      setIsRecalculatingBatch(false);

      if (result?.batch) {
        setBatch(result.batch);
        setBatchProductAmount(result.batch.amount);
      } else {
        notifications.show(
          'Failed to calculate product batch. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    }
  }, [batch?.id, batchProductAmount, recalculateBatchForProduct, notifications]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Item',
        width: 275,
        display: 'flex',
        valueGetter: (_, row) => row.inventoryItem?.item?.name || '',
        renderCell: (params) => {
          return <a href={`/dashboard/items/${params.row.inventoryItem?.itemId}`}>{params.row.inventoryItem?.item?.name}</a>
        }
      },
      {
        field: 'vendorName',
        headerName: 'Vendor',
        width: 275,
        display: 'flex',
        valueGetter: (_, row) => row.inventoryItem?.vendor?.name || '',
        renderCell: (params) => {
          return <a href={`/dashboard/vendors/${params.row.inventoryItem?.vendorId}`}>{params.row.inventoryItem?.vendor?.name || 'N/A'}</a>
        }
      },
      {
        field: 'lotNumber',
        headerName: 'Lot Number',
        width: 275,
        display: 'flex',
        valueGetter: (_, row) => row.inventoryItem?.lotNumber || '',
        renderCell: (params) => {
          return <a href={`/dashboard/inventory-items/${params.row.inventoryItemId}`}>{params.row.inventoryItem?.lotNumber || 'N/A'}</a>
        }
      },
      {
        field: 'quantityUsed',
        headerName: 'Qty Used (lbs)',
        type: 'number',
        width: 200,
        display: 'flex',
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'totalItemCost',
        headerName: 'Total Item Cost ($)',
        type: 'number',
        width: 200,
        display: 'flex',
        valueFormatter: (value) => toUSCurrency(value)
      }
    ],
    [],
  );

  const calculatedProductItemColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Item',
        display: 'flex',
        flex: .5,
        valueGetter: (_, row) => row.item?.name
      },
      {
        field: 'productBatchQuantity',
        headerName: 'Qty Needed (lbs)',
        type: 'number',
        display: 'flex',
        flex: 1,
        valueFormatter: (value: number | undefined) => (value || 0).toFixed(3)
      },
      {
        field: 'remainingProductBatchQuantity',
        headerName: 'Rem Qty Needed (lbs)',
        type: 'number',
        display: 'flex',
        flex: 1,
        valueFormatter: (value: number | undefined) => (value || 0).toFixed(3)
      },
      {
        field: 'calculatedItemCost',
        headerName: 'Total Item Cost ($)',
        type: 'number',
        display: 'flex',
        flex: 1,
        valueFormatter: (value: number | undefined) => toUSCurrency(value)
      },
      {
        field: 'calculatedConsumedInventoryItems',
        headerName: 'Calculated Inventory Items',
        sortable: false,
        flex: 4,
        minWidth: 600,
        renderCell: (params) => {
          return (
            (params.row.calculatedConsumedInventoryItems || []).length === 0 ?
              <Box sx={{ color: '#fac1c0' }}>
                No items available.
              </Box> : (
                <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="left">Vendor</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="left">Lot #</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="left">Date Ordered</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">QOH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">ROH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(params.row.calculatedConsumedInventoryItems || []).map((calculatedInventoryItem: ConsumedInventoryItem) => (
                      <TableRow
                        key={calculatedInventoryItem.inventoryItemId}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="left">
                          {calculatedInventoryItem.inventoryItem?.vendor?.name}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="left">
                          {calculatedInventoryItem.inventoryItem?.lotNumber || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="left">
                          {dayjs(calculatedInventoryItem.inventoryItem?.purchaseOrder?.dateTimePurchased).format('M/D/YYYY') || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          {(calculatedInventoryItem.inventoryItem?.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(calculatedInventoryItem.inventoryItem?.item?.unitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          {calculatedInventoryItem.inventoryItem?.remainingQuantityOnHand?.toFixed(3)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          ${toUSCurrency(calculatedInventoryItem.totalItemCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
          );
        }
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (!params.row.productBatchQuantityMet) {
      return 'product-batch-quantity-not-met';
    }

    return '';
  }

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return batch ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={batch?.isComplete ? { xs: 4, sm: 2 } : { xs: 6, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Product</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <a href={`/dashboard/products/${batch.product?.id}`}>{batch.product?.name}</a>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={batch?.isComplete ? { xs: 4, sm: 2 } : { xs: 6, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Customer</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(batch.product?.customerId || 0) > 0 ?
                  <a href={`/dashboard/customers/${batch.product?.customer?.id}`}>{batch.product?.customer?.name}</a> :
                  <>N/A</>
                }
              </Typography>
            </Paper>
          </Grid>
          {batch?.isComplete && (
            <>
              <Grid size={{ xs: 4, sm: 2 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Product Amount</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {batch.amount.toFixed(3)} lbs
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Lot Number</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {batch.lotNumber}
                  </Typography>
                </Paper>
              </Grid>
            </>
          )}
          <Grid size={batch?.isComplete ? { xs: 4, sm: 2 } : { xs: 6, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{batch.isComplete ? 'Total Cost' : 'Total Calculated Cost'}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ${toUSCurrency(batch.totalCost)}
              </Typography>
            </Paper>
          </Grid>
          {!batch?.isComplete && (
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%', pt: 2, pb: 1 }}>
                  <NumberField
                    size="small"
                    value={batchProductAmount || null}
                    onValueChange={(event) => onAmountChange(event, 'amount')}
                    name="amount"
                    label="Product Amount (lbs)"
                    error={batchProductAmount === undefined || batchProductAmount <= .1 || batchProductAmount > 1000}
                    weightFieldUnits="lbs"
                    fullWidth
                  />
                  <Box>
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      disabled={(batchProductAmount || 0) <= .1 || (batchProductAmount || 0) > 1000}
                      loading={isRecalculatingBatch}
                      onClick={recalculateBatch}
                    >
                      Recalculate
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          )}
          {batch?.isComplete && (
            <Grid size={{ xs: 4, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Notes</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {batch.notes || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              {batch?.isComplete === true ?
                <Typography variant="overline">
                  Consumed Items
                </Typography> :
                (batch?.calculatedProductItems?.some(productItem => !productItem.productBatchQuantityMet) ?
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ErrorOutlineOutlinedIcon fontSize="small" color="error" />
                    <Typography variant="overline" color="error">Calculated Items</Typography>
                  </Stack> :
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckOutlinedIcon fontSize="small" color="success" />
                    <Typography variant="overline" color="success">Calculated Items</Typography>
                  </Stack>
                )
              }
              {batch.isComplete ?
                <Box sx={{ height: '45vh', width: '100%' }}>
                  <DataGrid
                    density="compact"
                    rows={batch.consumedInventoryItems}
                    columns={columns}
                    loading={isLoading}
                    pageSizeOptions={[INITIAL_PAGE_SIZE]}
                    getRowHeight={() => 'auto'}
                    getRowClassName={(params) =>
                      params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                    }
                    sx={{
                      '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                      '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                      '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                      [`& .${gridClasses.columnHeader}, & .${gridClasses.cell} `]: {
                        outline: 'transparent',
                      },
                      [`& .${gridClasses.columnHeader}: focus - within, & .${gridClasses.cell}: focus - within`]:
                      {
                        outline: 'none',
                      },
                      [`& .${gridClasses.row}: hover`]: {
                        cursor: 'pointer',
                      },
                    }}
                    slotProps={{
                      loadingOverlay: {
                        variant: 'circular-progress',
                        noRowsVariant: 'circular-progress',
                      },
                      baseIconButton: {
                        size: 'small',
                      },
                    }}
                  />
                </Box> :
                <Box sx={{ height: '45vh', width: '100%' }}>
                  <DataGrid
                    density="compact"
                    rows={batch.calculatedProductItems}
                    columns={calculatedProductItemColumns}
                    loading={isLoading || isRecalculatingBatch}
                    pageSizeOptions={[INITIAL_PAGE_SIZE]}
                    getRowHeight={() => 'auto'}
                    getRowClassName={getRowClassName}
                    sx={[
                      () => ({
                        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                        [`& .${gridClasses.columnHeader}, & .${gridClasses.cell} `]: {
                          outline: 'transparent',
                        },
                        [`& .${gridClasses.columnHeader}: focus - within, & .${gridClasses.cell}: focus - within`]:
                        {
                          outline: 'none',
                        },
                        [`& .${gridClasses.row}: hover`]: {
                          cursor: 'pointer',
                        },
                        '.product-batch-quantity-not-met': {
                          backgroundColor: '#f8d7c0',
                          '&:hover': {
                            backgroundColor: 'rgba(248,215,192,.75)',
                            filter: 'brightness(105%)'
                          }
                        }
                      }),
                      (theme) =>
                        theme.applyStyles('dark', {
                          '.product-batch-quantity-not-met': {
                            backgroundColor: '#ed201c',
                            '&:hover': {
                              backgroundColor: '#ed201c',
                              filter: 'brightness(120%)'
                            }
                          }
                        })
                    ]}
                    slotProps={{
                      loadingOverlay: {
                        variant: 'circular-progress',
                        noRowsVariant: 'circular-progress',
                      },
                      baseIconButton: {
                        size: 'small',
                      },
                    }}
                  />
                </Box>
              }
            </Paper>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          {batch.isComplete ?
            <></> :
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Box sx={{ pt: '5px ' }}>
                <TextField
                  size="small"
                  value={notes}
                  onChange={onNotesChange}
                  name="notes"
                  label="Notes"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>
              <Box sx={{ paddingTop: .75 }}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={submitBatch}
                    disabled={batch?.calculatedProductItems?.some(productItem => !productItem.productBatchQuantityMet)}
                    loading={isRecalculatingBatch || isSubmitting}
                  >
                    Complete Batch
                  </Button>
                  <Button
                    variant="contained"
                    loading={isRecalculatingBatch}
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    loading={isRecalculatingBatch}
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            </Stack>
          }
        </Stack>
      </Box >
    ) : null;
  }, [isLoading, error, batch, batchProductAmount, isRecalculatingBatch, recalculateBatch, columns, calculatedProductItemColumns, handleBack, notes, onNotesChange, submitBatch, isSubmitting, handleEdit, handleDelete, onAmountChange]);

  const pageTitle = `${batch?.product?.name || 'N/A'} Batch ${batch?.isComplete ? `(Completed on ${dayjs(batch?.dateTimeCompleted).format('M/D/YYYY')})` : '(Not Completed)'} `;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Batches', path: '/dashboard/batches' },
        { title: `${batch?.product?.name || 'N/A'} Batch` },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
